// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  Middleware as AllocationMiddleware,
  PublicApi,
  RequestContext as AllocationRequestContext,
  ResponseContext as AllocationResponseContext,
  ServerConfiguration,
  createConfiguration,
} from '@digitalasset/allocation-api-ts-client';
import {
  Middleware as AllocationInstructionMiddleware,
  PublicApi as AllocationInstructionPublicApi,
  RequestContext as AllocationInstructionRequestContext,
  ResponseContext as AllocationInstructionResponseContext,
  ServerConfiguration as AllocationInstructionServerConfiguration,
  createConfiguration as allocationInstructionCreateConfiguration,
} from '@digitalasset/allocation-instruction-api-ts-client';
import {
  Middleware as TransferInstructionMiddleware,
  PublicApi as TransferInstructionPublicApi,
  RequestContext as TransferInstructionRequestContext,
  ResponseContext as TransferInstructionResponseContext,
  ServerConfiguration as TransferInstructionServerConfiguration,
  createConfiguration as transferInstructionCreateConfiguration,
} from '@digitalasset/transfer-instruction-api-ts-client';
import React, { useMemo } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { AllocationApiContext } from './AllocationApiContext';
import { AllocationInstructionApiContext } from './AllocationInstructionApiContext';
import { TransferInstructionApiContext } from './TransferInstructionApiContext';
import { useUserState } from './UserContext';

class AllocationApiMiddleware
  extends BaseApiMiddleware<AllocationRequestContext, AllocationResponseContext>
  implements AllocationMiddleware {}

class AllocationInstructionApiMiddleware
  extends BaseApiMiddleware<
    AllocationInstructionRequestContext,
    AllocationInstructionResponseContext
  >
  implements AllocationInstructionMiddleware {}

class TransferInstructionApiMiddleware
  extends BaseApiMiddleware<TransferInstructionRequestContext, TransferInstructionResponseContext>
  implements TransferInstructionMiddleware {}

interface AllocationApiProps {
  url: string;
}

export const AllocationApiProvider = ({
  url,
  children,
}: React.PropsWithChildren<AllocationApiProps>) => {
  const { loggedInUser } = useUserState();

  const clients = useMemo(() => {
    if (!loggedInUser) {
      return undefined;
    }
    const token = loggedInUser.userAccessToken;
    return {
      allocation: new PublicApi(
        createConfiguration({
          baseServer: new ServerConfiguration(url, {}),
          promiseMiddleware: [new AllocationApiMiddleware(token)],
        }),
      ),
      allocationInstruction: new AllocationInstructionPublicApi(
        allocationInstructionCreateConfiguration({
          baseServer: new AllocationInstructionServerConfiguration(url, {}),
          promiseMiddleware: [new AllocationInstructionApiMiddleware(token)],
        }),
      ),
      transferInstruction: new TransferInstructionPublicApi(
        transferInstructionCreateConfiguration({
          baseServer: new TransferInstructionServerConfiguration(url, {}),
          promiseMiddleware: [new TransferInstructionApiMiddleware(token)],
        }),
      ),
    };
  }, [url, loggedInUser]);

  return (
    <AllocationApiContext.Provider value={clients?.allocation}>
      <AllocationInstructionApiContext.Provider value={clients?.allocationInstruction}>
        <TransferInstructionApiContext.Provider value={clients?.transferInstruction}>
          {children}
        </TransferInstructionApiContext.Provider>
      </AllocationInstructionApiContext.Provider>
    </AllocationApiContext.Provider>
  );
};
